import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { LOGO_URL, LOGO_WHITE } from "@/lib/brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, MailCheck } from "lucide-react";
import api from "@/lib/api";

export default function Auth({ mode = "login" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, register } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "", phone: "", role: "buyer" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const inviteToken = searchParams.get("invite");

  useEffect(() => {
    const r = searchParams.get("role");
    if (r === "seller" || r === "buyer") setForm((f) => ({ ...f, role: r }));
  }, [searchParams]);

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/invitations/${inviteToken}`)
      .then(({ data }) => {
        setInvite(data);
        setForm((f) => ({ ...f, role: "seller", email: data.email, name: `${data.first_name} ${data.last_name}`, phone: data.phone || f.phone }));
      })
      .catch((e) => setInviteError(formatApiError(e.response?.data?.detail) || "Invitation invalide."));
  }, [inviteToken]);

  const homeFor = (u) => (u?.role === "admin" ? "/admin" : u?.role === "seller" ? "/vendeur" : "/dashboard");

  useEffect(() => {
    if (user) navigate(homeFor(user), { replace: true });
  }, [user, navigate]);

  const isRegister = mode === "register";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      let u;
      if (isRegister) {
        u = await register(invite ? { ...form, role: "seller", invite_token: inviteToken } : form);
      } else {
        u = await login(form.email, form.password);
      }
      navigate(homeFor(u), { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFDFC]">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1576731753569-3e93a228048c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1B2A4A]/85" />
        <div className="relative h-full flex flex-col p-12 text-white">
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <Link to="/"><img src={LOGO_WHITE} alt="PROAC Transmission" className="w-64 h-auto object-contain mb-10" /></Link>
            <h2 className="font-display text-4xl leading-tight">Accédez aux dossiers <span className="text-[#E0C173] italic">complets.</span></h2>
            <p className="mt-5 text-white/70 max-w-md">Vidéos de présentation, chiffres détaillés, prix de cession et contact direct avec nos conseillers — réservés à nos acheteurs enregistrés.</p>
          </div>
          <p className="text-white/40 text-xs text-center">© {new Date().getFullYear()} PROAC Transmission</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8"><img src={LOGO_URL} alt="PROAC Transmission" className="h-12 w-auto object-contain" /></Link>
          <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">{isRegister ? (form.role === "seller" ? "Espace cédant" : "Profil acheteur") : "Espace client"}</span>
          <h1 className="mt-3 font-display text-3xl text-[#1B2A4A]">{isRegister ? "Créer mon compte" : "Connexion"}</h1>

          {inviteError && (
            <div data-testid="invite-error" className="mt-6 bg-[#F8F6F0] border-l-4 border-[#E0C173] p-5 text-sm text-[#1B2A4A] space-y-3">
              <p>{inviteError}</p>
              <Link to="/connexion" data-testid="invite-error-login" className="inline-block bg-[#1B2A4A] text-white px-5 py-2 rounded-full">Se connecter</Link>
            </div>
          )}
          {inviteError ? null : invite ? (
            <div data-testid="invite-banner" className="mt-6 mb-2 flex gap-3 bg-[#F8F6F0] border-l-4 border-[#E0C173] p-4 text-sm text-[#1B2A4A]">
              <MailCheck className="w-5 h-5 text-[#C9A85A] shrink-0" strokeWidth={1.5} />
              <div>Bonjour <strong>{invite.first_name}</strong>, {invite.kind === "onsite"
                ? "votre espace cédant a été créé lors de notre rendez-vous. Choisissez simplement votre mot de passe pour retrouver la fiche de votre entreprise et la compléter si besoin. Elle reste privée tant que PROAC Transmission n'a pas validé sa publication."
                : "PROAC Transmission vous invite à créer votre espace cédant. Choisissez simplement un mot de passe, puis complétez la fiche de votre entreprise : elle reste privée tant que vous et PROAC n'avez pas validé sa publication."}</div>
            </div>
          ) : <div className="mt-8" />}

          <form onSubmit={submit} className={`space-y-4 ${inviteError ? "hidden" : ""}`}>
            {isRegister && (
              <>
                <div className={invite ? "hidden" : ""}>
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Je suis</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    <button type="button" data-testid="role-buyer" onClick={() => setForm({ ...form, role: "buyer" })} className={`py-3 px-3 text-sm border text-center transition-colors rounded-full ${form.role === "buyer" ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-white text-[#1B2A4A] border-gray-300 hover:border-[#1B2A4A]"}`}>Acheteur / Repreneur</button>
                    <button type="button" data-testid="role-seller" onClick={() => setForm({ ...form, role: "seller" })} className={`py-3 px-3 text-sm border text-center transition-colors rounded-full ${form.role === "seller" ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-white text-[#1B2A4A] border-gray-300 hover:border-[#1B2A4A]"}`}>Entreprise vendeuse</button>
                  </div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-[#475569]">Nom complet</Label><Input data-testid="register-name" required value={form.name} onChange={set("name")} className="rounded-none mt-1.5 border-gray-300" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider text-[#475569]">Société (option.)</Label><Input data-testid="register-company" value={form.company} onChange={set("company")} className="rounded-none mt-1.5 border-gray-300" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-[#475569]">Téléphone</Label><Input data-testid="register-phone" value={form.phone} onChange={set("phone")} className="rounded-none mt-1.5 border-gray-300" /></div>
                </div>
              </>
            )}
            <div><Label className="text-xs uppercase tracking-wider text-[#475569]">Email</Label><Input data-testid="auth-email" type="email" required readOnly={!!invite} value={form.email} onChange={set("email")} className="rounded-none mt-1.5 border-gray-300" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-[#475569]">Mot de passe</Label><Input data-testid="auth-password" type="password" required minLength={isRegister ? 8 : undefined} placeholder={isRegister ? "8 caractères minimum" : undefined} value={form.password} onChange={set("password")} className="rounded-none mt-1.5 border-gray-300" /></div>

            {error && <p data-testid="auth-error" className="text-sm text-red-600">{error}</p>}

            <Button data-testid="auth-submit" type="submit" disabled={busy} className="w-full rounded-full bg-[#1B2A4A] hover:bg-[#0D1629] py-6 text-base">
              <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} /> {busy ? "Veuillez patienter…" : isRegister ? (form.role === "seller" ? "Créer mon espace cédant" : "Créer mon profil acheteur") : "Se connecter"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-[#475569] text-center">
            {isRegister ? (
              <>Déjà un compte ? <Link to="/connexion" data-testid="switch-login" className="text-[#E0C173] font-medium">Se connecter</Link></>
            ) : (
              <>Pas encore de compte ? <Link to="/inscription" data-testid="switch-register" className="text-[#E0C173] font-medium">Devenir acheteur</Link></>
            )}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
